import React, { useState } from 'react';
import { Table, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faEdit,
  faTrash,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';

const StyledTh = styled.th`
  cursor: pointer;
`;

const StyledRow = styled.tr`
  &:hover {
    background-color: rgba(0, 123, 255, 0.1);
  }
`;

const StatusBadge = styled(Badge)`
  font-size: 0.8em;
`;

const ActionButton = styled(Button)`
  margin: 0 2px;
  padding: 0.25rem 0.5rem;
`;

const StorageItemsList = ({
  items,
  onEdit,
  onDelete,
  onUpdateQuantity,
  showPrice = true,
  showMinQuantity = true,
  showDesiredQuantity = true,
}) => {
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const sortedItems = items
    ? [...items].sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];

        // Obsługa sortowania producentów (array)
        if (sortBy === 'producers') {
          aValue = a.producers?.map((p) => p.name).join(', ') || '';
          bValue = b.producers?.map((p) => p.name).join(', ') || '';
        }

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      })
    : [];

  const getQuantityStatus = (quantity, minQuantity) => {
    if (quantity === 0) {
      return { text: 'Brak', variant: 'danger' };
    } else if (quantity <= minQuantity) {
      return { text: 'Niski stan', variant: 'warning' };
    } else {
      return { text: 'Dostępny', variant: 'success' };
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">Brak produktów magazynowych</p>
      </div>
    );
  }

  return (
    <div>
      <Table responsive="lg" striped bordered hover size="sm">
        <thead>
          <tr>
            <th>LP</th>
            <StyledTh onClick={() => handleSort('type')}>
              Typ <FontAwesomeIcon icon={faSort} />
            </StyledTh>
            <StyledTh onClick={() => handleSort('name')}>
              Nazwa <FontAwesomeIcon icon={faSort} />
            </StyledTh>
            {showPrice && (
              <StyledTh onClick={() => handleSort('price')}>
                Cena (PLN) <FontAwesomeIcon icon={faSort} />
              </StyledTh>
            )}
            <StyledTh onClick={() => handleSort('quantity')}>
              Na stanie <FontAwesomeIcon icon={faSort} />
            </StyledTh>
            {showMinQuantity && (
              <StyledTh onClick={() => handleSort('minQuantity')}>
                Min. ilość <FontAwesomeIcon icon={faSort} />
              </StyledTh>
            )}
            {showDesiredQuantity && (
              <StyledTh onClick={() => handleSort('desiredQuantity')}>
                Pożądana ilość <FontAwesomeIcon icon={faSort} />
              </StyledTh>
            )}
            <th>Status</th>
            <StyledTh onClick={() => handleSort('producers')}>
              Producenci <FontAwesomeIcon icon={faSort} />
            </StyledTh>
            <th>Akcje</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((item, index) => {
            const status = getQuantityStatus(
              item.quantity,
              item.minQuantity,
            );

            return (
              <StyledRow key={item._id}>
                <td>{index + 1}</td>
                <td>
                  <Badge bg="secondary">{item.type}</Badge>
                </td>
                <td>
                  <strong>{item.name}</strong>
                </td>
                {showPrice && (
                  <td>{item.price?.toFixed(2) || '0.00'} PLN</td>
                )}
                <td>
                  <strong
                    className={
                      item.quantity === 0
                        ? 'text-danger'
                        : item.quantity <= item.minQuantity
                        ? 'text-warning'
                        : 'text-success'
                    }
                  >
                    {item.quantity || 0}
                  </strong>
                </td>
                {showMinQuantity && <td>{item.minQuantity || 0}</td>}
                {showDesiredQuantity && (
                  <td>
                    <strong className="text-info">
                      {item.desiredQuantity || 0}
                    </strong>
                  </td>
                )}
                <td>
                  <StatusBadge bg={status.variant}>
                    {status.text}
                  </StatusBadge>
                </td>
                <td>
                  {item.producers && item.producers.length > 0 ? (
                    <small>
                      {item.producers.map((p) => p.name).join(', ')}
                    </small>
                  ) : (
                    <small className="text-muted">Brak</small>
                  )}
                </td>
                <td>
                  {onUpdateQuantity && (
                    <ActionButton
                      variant="outline-info"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateQuantity(item);
                      }}
                      title="Aktualizuj stan"
                    >
                      <FontAwesomeIcon icon={faWarehouse} />
                    </ActionButton>
                  )}
                  {onEdit && (
                    <ActionButton
                      variant="outline-primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(item);
                      }}
                      title="Edytuj"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </ActionButton>
                  )}
                  {onDelete && (
                    <ActionButton
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(item);
                      }}
                      title="Usuń"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </ActionButton>
                  )}
                </td>
              </StyledRow>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default StorageItemsList;
